import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Clock, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [appName, setAppName] = useState('Regulisto');
  const { language } = useLanguage();

  useEffect(() => {
    const fetchAppName = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/settings/public`);
        if (response.data?.app_name) {
          setAppName(response.data.app_name);
        }
      } catch (error) {
        console.error('Error fetching public settings:', error);
      }
    };
    fetchAppName();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      // Always show success to prevent email enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="forgot-password-page">
      <Card className="w-full max-w-md shadow-lg border-slate-200">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold font-['Manrope'] text-slate-900">{appName}</span>
          </div>
          
          {sent ? (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-xl">
                {language === 'es' ? '¡Revisa tu correo!' : 'Check your email!'}
              </CardTitle>
              <CardDescription>
                {language === 'es' 
                  ? 'Si existe una cuenta con ese email, recibirás instrucciones para restablecer tu contraseña.'
                  : 'If an account exists with that email, you will receive instructions to reset your password.'}
              </CardDescription>
            </>
          ) : (
            <>
              <CardTitle className="text-xl">
                {language === 'es' ? 'Recuperar contraseña' : 'Reset password'}
              </CardTitle>
              <CardDescription>
                {language === 'es' 
                  ? 'Introduce tu email y te enviaremos un enlace para restablecer tu contraseña'
                  : 'Enter your email and we will send you a link to reset your password'}
              </CardDescription>
            </>
          )}
        </CardHeader>
        
        <CardContent>
          {sent ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-500 text-center">
                {language === 'es' 
                  ? 'El enlace expirará en 1 hora. Si no recibes el email, revisa la carpeta de spam.'
                  : 'The link will expire in 1 hour. If you don\'t receive the email, check your spam folder.'}
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  {language === 'es' ? 'Volver al inicio de sesión' : 'Back to login'}
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">{language === 'es' ? 'Email' : 'Email'}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="nombre@empresa.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                disabled={loading}
                data-testid="forgot-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {language === 'es' ? 'Enviando...' : 'Sending...'}
                  </span>
                ) : (
                  language === 'es' ? 'Enviar enlace' : 'Send link'
                )}
              </Button>

              <Link to="/login">
                <Button variant="ghost" className="w-full gap-2 mt-2">
                  <ArrowLeft className="w-4 h-4" />
                  {language === 'es' ? 'Volver al inicio de sesión' : 'Back to login'}
                </Button>
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
